import {
  DEFAULT_DOOR,
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS_M,
  DEFAULT_WINDOW,
} from './floorPlanDesignerState';
import { projectPointOntoSegment } from './floorPlanDesignerGeometry';

export const AXIS_SNAP_DEG = 7;
export const NODE_MERGE_TOLERANCE_M = 0.15;
export const INTERSECTION_SNAP_TOLERANCE_M = 0.2;
export const WALL_INTERSECTION_TOLERANCE_M = 0.05;
export const NODE_TO_WALL_SNAP_TOLERANCE_M = 0.15;
export const MIN_WALL_LENGTH_M = 0.05;
export const OPENING_TO_WALL_SNAP_TOLERANCE_M = 0.35;

const EPS = 1e-6;

function metersToDesignerUnits(meters, scale) {
  const pixelsPerMeter = Number(scale?.pixelsPerMeter);
  if (Number.isFinite(pixelsPerMeter) && pixelsPerMeter > 0) {
    return meters * pixelsPerMeter;
  }
  return meters;
}

function designerUnitsToMeters(units, scale) {
  const metersPerPixel = Number(scale?.metersPerPixel);
  if (Number.isFinite(metersPerPixel) && metersPerPixel > 0) {
    return units * metersPerPixel;
  }
  return units;
}

function pointToTuple(point) {
  return [point.x, point.y];
}

function distancePoints(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function distanceTuples(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

function makePointMap(points) {
  return new Map(points.map((point) => [point.id, point]));
}

function cloneGeometry(points, walls) {
  return {
    points: points.map((point) => ({ ...point })),
    walls: walls.map((wall) => ({
      ...wall,
      thicknessMeters: wall.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M,
      color: wall.color ?? DEFAULT_WALL_COLOR,
      visibleIn3D: wall.visibleIn3D ?? true,
    })),
  };
}

function shortestAngleDiffDeg(a, b) {
  const normalizedA = ((a % 360) + 360) % 360;
  const normalizedB = ((b % 360) + 360) % 360;
  const diff = Math.abs(normalizedA - normalizedB);
  return Math.min(diff, 360 - diff);
}

function lineIntersection(a, b, c, d) {
  const rx = b[0] - a[0];
  const ry = b[1] - a[1];
  const sx = d[0] - c[0];
  const sy = d[1] - c[1];
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < EPS) return null;

  const qx = c[0] - a[0];
  const qy = c[1] - a[1];
  const t = (qx * sy - qy * sx) / denom;
  const u = (qx * ry - qy * rx) / denom;

  return {
    point: [a[0] + t * rx, a[1] + t * ry],
    t,
    u,
  };
}

function isWithinSegment(t, tolerance = 0) {
  return t >= -tolerance && t <= 1 + tolerance;
}

function createStats() {
  return {
    mergedPointsCount: 0,
    snappedIntersectionsCount: 0,
    splitWallsCount: 0,
    snappedNodesToWallsCount: 0,
    removedTinyWallsCount: 0,
    removedDuplicateWallsCount: 0,
  };
}

export function snapWallsToAxis(points, walls, options = {}) {
  const toleranceDeg = options.axisSnapDeg ?? AXIS_SNAP_DEG;
  const pointMap = makePointMap(points);

  for (const wall of walls) {
    const start = pointMap.get(wall.startPointId);
    const end = pointMap.get(wall.endPointId);
    if (!start || !end) continue;

    const angle = (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI;
    if (shortestAngleDiffDeg(angle, 0) <= toleranceDeg || shortestAngleDiffDeg(angle, 180) <= toleranceDeg) {
      const y = (start.y + end.y) / 2;
      start.y = y;
      end.y = y;
    } else if (shortestAngleDiffDeg(angle, 90) <= toleranceDeg || shortestAngleDiffDeg(angle, 270) <= toleranceDeg) {
      const x = (start.x + end.x) / 2;
      start.x = x;
      end.x = x;
    }
  }

  return { points, walls };
}

export function cleanupWalls(points, walls, options = {}) {
  const scale = options.scale;
  const minWallLength = metersToDesignerUnits(options.minWallLengthM ?? MIN_WALL_LENGTH_M, scale);
  const pointMap = makePointMap(points);
  const usedPointIds = new Set();
  const seen = new Set();
  const cleanWalls = [];
  const stats = options.stats ?? createStats();

  for (const wall of walls) {
    const start = pointMap.get(wall.startPointId);
    const end = pointMap.get(wall.endPointId);
    if (!start || !end || wall.startPointId === wall.endPointId) {
      stats.removedTinyWallsCount += 1;
      continue;
    }
    if (distancePoints(start, end) < minWallLength) {
      stats.removedTinyWallsCount += 1;
      continue;
    }

    const key = [wall.startPointId, wall.endPointId].sort().join('|');
    if (seen.has(key)) {
      stats.removedDuplicateWallsCount += 1;
      continue;
    }
    seen.add(key);
    usedPointIds.add(wall.startPointId);
    usedPointIds.add(wall.endPointId);
    cleanWalls.push(wall);
  }

  return {
    points: points.filter((point) => usedPointIds.has(point.id)),
    walls: cleanWalls,
  };
}

export function mergeNearbyPoints(points, walls, options = {}) {
  const scale = options.scale;
  const tolerance = metersToDesignerUnits(options.nodeMergeToleranceM ?? NODE_MERGE_TOLERANCE_M, scale);
  const stats = options.stats ?? createStats();
  const parent = new Map(points.map((point) => [point.id, point.id]));

  function find(id) {
    const current = parent.get(id);
    if (current === id) return id;
    const root = find(current);
    parent.set(id, root);
    return root;
  }

  function union(a, b) {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA === rootB) return false;
    parent.set(rootB, rootA);
    stats.mergedPointsCount += 1;
    return true;
  }

  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      if (distancePoints(points[i], points[j]) <= tolerance) {
        union(points[i].id, points[j].id);
      }
    }
  }

  const clusters = new Map();
  for (const point of points) {
    const root = find(point.id);
    if (!clusters.has(root)) clusters.set(root, []);
    clusters.get(root).push(point);
  }

  const idMap = new Map();
  const mergedPoints = [];
  for (const [root, cluster] of clusters.entries()) {
    const x = cluster.reduce((sum, point) => sum + point.x, 0) / cluster.length;
    const y = cluster.reduce((sum, point) => sum + point.y, 0) / cluster.length;
    mergedPoints.push({ id: root, x, y });
    cluster.forEach((point) => idMap.set(point.id, root));
  }

  const mergedWalls = walls.map((wall) => ({
    ...wall,
    startPointId: idMap.get(wall.startPointId) ?? wall.startPointId,
    endPointId: idMap.get(wall.endPointId) ?? wall.endPointId,
  }));

  return cleanupWalls(mergedPoints, mergedWalls, { ...options, stats });
}

export function snapEndpointsToIntersections(points, walls, options = {}) {
  const scale = options.scale;
  const tolerance = metersToDesignerUnits(options.intersectionSnapToleranceM ?? INTERSECTION_SNAP_TOLERANCE_M, scale);
  const stats = options.stats ?? createStats();
  const pointMap = makePointMap(points);

  for (let i = 0; i < walls.length; i += 1) {
    for (let j = i + 1; j < walls.length; j += 1) {
      const wallA = walls[i];
      const wallB = walls[j];
      const a0 = pointMap.get(wallA.startPointId);
      const a1 = pointMap.get(wallA.endPointId);
      const b0 = pointMap.get(wallB.startPointId);
      const b1 = pointMap.get(wallB.endPointId);
      if (!a0 || !a1 || !b0 || !b1) continue;

      const hit = lineIntersection(pointToTuple(a0), pointToTuple(a1), pointToTuple(b0), pointToTuple(b1));
      if (!hit || !isWithinSegment(hit.t, 0.05) || !isWithinSegment(hit.u, 0.05)) continue;

      for (const pointId of [wallA.startPointId, wallA.endPointId, wallB.startPointId, wallB.endPointId]) {
        const point = pointMap.get(pointId);
        if (!point) continue;
        if (distanceTuples(pointToTuple(point), hit.point) <= tolerance) {
          point.x = hit.point[0];
          point.y = hit.point[1];
          stats.snappedIntersectionsCount += 1;
        }
      }
    }
  }

  return { points, walls };
}

function addIntersectionPoint(points, point, tolerance) {
  for (const existing of points) {
    if (distanceTuples(pointToTuple(existing), point) <= tolerance) return existing.id;
  }

  const id = `import_i_${points.length + 1}`;
  points.push({ id, x: point[0], y: point[1] });
  return id;
}

function splitWallsByPoints(points, walls, splitMap, options = {}) {
  const stats = options.stats ?? createStats();
  const pointMap = makePointMap(points);
  const nextWalls = [];

  for (const wall of walls) {
    const splitPointIds = splitMap.get(wall.id) || [];
    if (!splitPointIds.length) {
      nextWalls.push(wall);
      continue;
    }

    const start = pointMap.get(wall.startPointId);
    const end = pointMap.get(wall.endPointId);
    if (!start || !end) continue;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < EPS) continue;

    const ordered = [
      { id: wall.startPointId, t: 0 },
      ...splitPointIds
        .map((id) => {
          const point = pointMap.get(id);
          if (!point) return null;
          const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lenSq;
          return { id, t };
        })
        .filter(Boolean)
        .filter((entry) => entry.t > 0.01 && entry.t < 0.99),
      { id: wall.endPointId, t: 1 },
    ].sort((a, b) => a.t - b.t);

    for (let i = 0; i < ordered.length - 1; i += 1) {
      if (ordered[i].id === ordered[i + 1].id) continue;
      nextWalls.push({
        ...wall,
        id: `${wall.id}_split_${i}`,
        startPointId: ordered[i].id,
        endPointId: ordered[i + 1].id,
      });
      stats.splitWallsCount += 1;
    }
  }

  return nextWalls;
}

export function splitWallsAtIntersections(points, walls, options = {}) {
  const scale = options.scale;
  const tolerance = metersToDesignerUnits(options.wallIntersectionToleranceM ?? WALL_INTERSECTION_TOLERANCE_M, scale);
  const splitMap = new Map();
  const pointMap = makePointMap(points);

  for (let i = 0; i < walls.length; i += 1) {
    for (let j = i + 1; j < walls.length; j += 1) {
      const wallA = walls[i];
      const wallB = walls[j];
      if (
        wallA.startPointId === wallB.startPointId ||
        wallA.startPointId === wallB.endPointId ||
        wallA.endPointId === wallB.startPointId ||
        wallA.endPointId === wallB.endPointId
      ) {
        continue;
      }

      const a0 = pointMap.get(wallA.startPointId);
      const a1 = pointMap.get(wallA.endPointId);
      const b0 = pointMap.get(wallB.startPointId);
      const b1 = pointMap.get(wallB.endPointId);
      if (!a0 || !a1 || !b0 || !b1) continue;

      const hit = lineIntersection(pointToTuple(a0), pointToTuple(a1), pointToTuple(b0), pointToTuple(b1));
      if (!hit || hit.t <= 0.01 || hit.t >= 0.99 || hit.u <= 0.01 || hit.u >= 0.99) continue;

      const pointId = addIntersectionPoint(points, hit.point, tolerance);
      if (!splitMap.has(wallA.id)) splitMap.set(wallA.id, []);
      if (!splitMap.has(wallB.id)) splitMap.set(wallB.id, []);
      splitMap.get(wallA.id).push(pointId);
      splitMap.get(wallB.id).push(pointId);
    }
  }

  if (splitMap.size === 0) return { points, walls };

  return {
    points,
    walls: splitWallsByPoints(points, walls, splitMap, options),
  };
}

export function snapNodesToWallsAndSplit(points, walls, options = {}) {
  const scale = options.scale;
  const tolerance = metersToDesignerUnits(options.nodeToWallSnapToleranceM ?? NODE_TO_WALL_SNAP_TOLERANCE_M, scale);
  const stats = options.stats ?? createStats();
  const pointMap = makePointMap(points);
  const splitMap = new Map();

  for (const point of points) {
    let best = null;
    for (const wall of walls) {
      if (wall.startPointId === point.id || wall.endPointId === point.id) continue;
      const start = pointMap.get(wall.startPointId);
      const end = pointMap.get(wall.endPointId);
      if (!start || !end) continue;

      const projected = projectPointOntoSegment(pointToTuple(start), pointToTuple(end), [point.x, point.y]);
      if (projected.t <= 0.01 || projected.t >= 0.99 || projected.dist > tolerance) continue;
      if (!best || projected.dist < best.dist) {
        best = { wallId: wall.id, projected };
      }
    }

    if (!best) continue;
    point.x = best.projected.projected[0];
    point.y = best.projected.projected[1];
    if (!splitMap.has(best.wallId)) splitMap.set(best.wallId, []);
    splitMap.get(best.wallId).push(point.id);
    stats.snappedNodesToWallsCount += 1;
  }

  if (splitMap.size === 0) return { points, walls };

  return {
    points,
    walls: splitWallsByPoints(points, walls, splitMap, options),
  };
}

export function rebuildIds(points, walls) {
  const pointIdMap = new Map();
  const nextPoints = points.map((point, index) => {
    const id = `p_${index + 1}`;
    pointIdMap.set(point.id, id);
    return { ...point, id };
  });

  const nextWalls = walls.map((wall, index) => ({
    ...wall,
    id: `w_${index + 1}`,
    startPointId: pointIdMap.get(wall.startPointId),
    endPointId: pointIdMap.get(wall.endPointId),
  }));

  return {
    points: nextPoints,
    walls: nextWalls.filter((wall) => wall.startPointId && wall.endPointId && wall.startPointId !== wall.endPointId),
    pointIdMap,
    wallIdMap: new Map(walls.map((wall, index) => [wall.id, `w_${index + 1}`])),
  };
}

function nearestWallForOpening(center, points, walls, scale, options = {}) {
  const tolerance = metersToDesignerUnits(options.openingToWallSnapToleranceM ?? OPENING_TO_WALL_SNAP_TOLERANCE_M, scale);
  const pointMap = makePointMap(points);
  let best = null;

  for (const wall of walls) {
    const start = pointMap.get(wall.startPointId);
    const end = pointMap.get(wall.endPointId);
    if (!start || !end) continue;
    const projected = projectPointOntoSegment(pointToTuple(start), pointToTuple(end), center);
    if (projected.dist > tolerance) continue;
    if (!best || projected.dist < best.dist) {
      best = { wall, start: pointToTuple(start), end: pointToTuple(end), ...projected };
    }
  }

  return best;
}

function getPointBounds(points = []) {
  if (!points.length) return null;
  const xs = points.map((point) => Number(point.x)).filter(Number.isFinite);
  const ys = points.map((point) => Number(point.y)).filter(Number.isFinite);
  if (!xs.length || !ys.length) return null;

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    minX,
    maxX,
    minY,
    maxY,
    widthM: maxX - minX,
    heightM: maxY - minY,
  };
}

function getMeterBoundsFromPointBounds(bounds, scale) {
  if (!bounds) return null;
  const metersPerPixel = Number(scale?.metersPerPixel);
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return bounds;

  return {
    minX: bounds.minX * metersPerPixel,
    maxX: bounds.maxX * metersPerPixel,
    minY: bounds.minY * metersPerPixel,
    maxY: bounds.maxY * metersPerPixel,
    widthM: bounds.widthM * metersPerPixel,
    heightM: bounds.heightM * metersPerPixel,
  };
}

export function convertAndSnapOpeningsToWalls(rawOpenings = [], points, walls, scale, options = {}) {
  const metersPerPixel = Number(scale?.metersPerPixel);
  const offsetXPx = Number(scale?.offsetXPx) || 0;
  const offsetYPx = Number(scale?.offsetYPx) || 0;
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return [];

  const openings = [];
  for (const rawOpening of rawOpenings) {
    const type = rawOpening?.type === 'window' ? 'window' : rawOpening?.type === 'door' ? 'door' : null;
    if (!type || !rawOpening?.center) continue;

    const centerX = Number(rawOpening.center.x);
    const centerY = Number(rawOpening.center.y);
    if (!Number.isFinite(centerX) || !Number.isFinite(centerY)) continue;

    const center = [centerX + offsetXPx, centerY + offsetYPx];
    const nearest = nearestWallForOpening(center, points, walls, scale, options);
    if (!nearest) continue;

    const defaults = type === 'door' ? DEFAULT_DOOR : DEFAULT_WINDOW;
    const widthMeters = designerUnitsToMeters(Number(rawOpening.widthPixels), scale);
    const wallLengthM = designerUnitsToMeters(
      Math.hypot(nearest.end[0] - nearest.start[0], nearest.end[1] - nearest.start[1]),
      scale,
    );
    const halfRatio =
      wallLengthM > 0
        ? Math.min(0.49, ((Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : defaults.widthMeters) / 2) / wallLengthM)
        : 0;

    openings.push({
      id: `${type}_${openings.length + 1}`,
      type,
      wallId: nearest.wall.id,
      centerRatio: Math.max(halfRatio, Math.min(1 - halfRatio, nearest.t)),
      widthMeters: Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : defaults.widthMeters,
      heightMeters: defaults.heightMeters,
      sillHeightMeters: defaults.sillHeightMeters,
    });
  }

  return openings;
}

export function normalizeImportedFloorPlanGeometry(input, options = {}) {
  const stats = createStats();
  const scale = input?.scale || {};
  const rawOpenings = Array.isArray(input?.rawOpenings) ? input.rawOpenings : [];
  let { points, walls } = cloneGeometry(input?.points || [], input?.walls || []);
  const normalizedOptions = { ...options, scale, stats };

  ({ points, walls } = snapWallsToAxis(points, walls, normalizedOptions));
  ({ points, walls } = mergeNearbyPoints(points, walls, normalizedOptions));
  ({ points, walls } = snapWallsToAxis(points, walls, normalizedOptions));
  ({ points, walls } = snapEndpointsToIntersections(points, walls, normalizedOptions));
  ({ points, walls } = splitWallsAtIntersections(points, walls, normalizedOptions));
  ({ points, walls } = snapNodesToWallsAndSplit(points, walls, normalizedOptions));
  ({ points, walls } = mergeNearbyPoints(points, walls, normalizedOptions));
  ({ points, walls } = cleanupWalls(points, walls, normalizedOptions));
  ({ points, walls } = rebuildIds(points, walls));
  const openings = convertAndSnapOpeningsToWalls(rawOpenings, points, walls, scale, options);
  const coordinateBounds = getPointBounds(points);
  const meterBounds = getMeterBoundsFromPointBounds(coordinateBounds, scale);
  const nextScale = {
    ...scale,
    coordinateBounds,
    meterBounds,
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('Imported geometry normalization', {
      inputPoints: input?.points?.length || 0,
      inputWalls: input?.walls?.length || 0,
      outputPoints: points.length,
      outputWalls: walls.length,
      importedOpenings: openings.length,
      mergedPointsCount: stats.mergedPointsCount,
      snappedIntersectionsCount: stats.snappedIntersectionsCount,
      splitWallsCount: stats.splitWallsCount,
      snappedNodesToWallsCount: stats.snappedNodesToWallsCount,
      removedTinyWallsCount: stats.removedTinyWallsCount,
      removedDuplicateWallsCount: stats.removedDuplicateWallsCount,
    });
  }

  return {
    points,
    walls,
    openings,
    scale: nextScale,
    rawOpenings,
  };
}
